import { AnimatePresence, motion } from "motion/react";

/**
 * Parse a tone string like "Warm, Friendly and Approachable" into an array of tags.
 */
export const parseTone = (str) =>
  str
    ? str
        .split(/[,;]|\band\b/i)
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

/**
 * Extract a clean domain from a URL string.
 */
export const extractDomain = (url) => {
  if (!url) return "";
  try {
    const hostname = new URL(url.startsWith("http") ? url : `https://${url}`)
      .hostname;
    return hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export const BrandProfileCard = ({
  businessInfo,
  currentState = 0,
  loadingStates = [],
  url,
  isStatic = false,
}) => {
  const isSectionVisible = (key) => {
    if (isStatic) return !!businessInfo?.[key];
    const idx = loadingStates.findIndex((s) => s.key === key);
    return idx !== -1 && idx <= currentState && !!businessInfo?.[key];
  };

  const hasLogo = isSectionVisible("companyLogo") && businessInfo?.companyLogo;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
      className="w-full rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
    >
      {/* Card Header */}
      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42"
            />
          </svg>
          <span className="text-sm font-semibold text-gray-900">
            Brand Profile
          </span>
        </div>
        {!isStatic && (
          <span className="px-2 py-0.5 bg-green-100 text-[10px] font-semibold text-green-700 rounded-full">
            Auto-detected
          </span>
        )}
      </div>

      {/* Card Body */}
      <div className="p-5 space-y-5">
        {/* Loader when body is empty */}
        {!isSectionVisible("companyName") &&
          !isSectionVisible("companyDescription") &&
          !isSectionVisible("companyColors") &&
          !isSectionVisible("companyTone") && (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
          )}

        {/* Logo + Name */}
        <AnimatePresence>
          {isSectionVisible("companyName") && (
            <motion.div
              key="section-logo"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2.5">
                Logo
              </p>
              <div className="flex items-center gap-3">
                {hasLogo ? (
                  <motion.img
                    key="logo-img"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                    src={businessInfo.companyLogo}
                    alt={businessInfo.companyName}
                    className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-primary/30">
                    <span className="text-lg font-bold text-secondary">
                      {businessInfo.companyName?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {businessInfo.companyName}
                  </p>
                  {url && (
                    <p className="text-xs text-gray-400">
                      {extractDomain(url)}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Description */}
        <AnimatePresence>
          {isSectionVisible("companyDescription") && (
            <motion.div
              key="section-description"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2.5">
                Description
              </p>
              <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                {businessInfo.companyDescription}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Brand Colors */}
        <AnimatePresence>
          {isSectionVisible("companyColors") &&
            Array.isArray(businessInfo.companyColors) &&
            businessInfo.companyColors.length > 0 && (
              <motion.div
                key="section-colors"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2.5">
                  Brand Colors
                </p>
                <div className="flex gap-3">
                  {businessInfo.companyColors.map((color, idx) => (
                    <motion.div
                      key={color + idx}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: idx * 0.06 }}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <div
                        className="w-10 h-10 rounded-lg"
                        style={{
                          backgroundColor: color,
                          border: "1px solid #e5e7eb",
                        }}
                      />
                      <span className="text-[10px] text-gray-400 font-mono">
                        {color}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
        </AnimatePresence>

        {/* Tone of Voice */}
        <AnimatePresence>
          {isSectionVisible("companyTone") && (
            <motion.div
              key="section-tone"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2.5">
                Tone of Voice
              </p>
              <div className="flex flex-wrap gap-1.5">
                {parseTone(businessInfo.companyTone).map((tag, idx) => (
                  <motion.span
                    key={tag}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: idx * 0.08 }}
                    className="px-2.5 py-1 bg-gray-100 text-xs font-medium text-gray-600 rounded-full"
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
